import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Divider,
  Box,
  Grid,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import QuestionBlock from "./QuestionBlock";
import axios from "axios";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import { motion } from "framer-motion";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import CustomTable from "../../Components/CustomTable/CustomTable";
import Swal from "sweetalert2";
import { debounce } from "lodash";
import AssessmentViewDrawer from "./AssessmentViewDrawer";
import { GridVisibilityOffIcon } from "@mui/x-data-grid";
import { DeleteForever, Edit, Visibility } from "@mui/icons-material";

const CreateAssessmentPage = () => {
  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const firstEditLoad = useRef(true);

  const { user_id, company_id, noRoomService } = useSelector(
    (state) => state.GlobalVariables
  );
  const handlePopup = () => {
    try {
      if (isCreate === true) {
        clear();
      }
      setIsCreate(!isCreate);
    } catch (error) {
      console.log(error);
    }
  };
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    total_marks: 0,
    total_questions: 0,
    company_id: company_id,
    questions: [],
    created_by: user_id,
  });

  const handleAddQuestion = () => {
    setFormData((prev) => {
      const newQuestion = {
        question_text: "",
        marks: 0,
        negative_marks: 0,
        type: "mcq",
        sort_order: prev.questions.length + 1, // 👈 Assign default value here
        options: [
          { option_text: "", is_correct: false, sort_order: 1 },
          { option_text: "", is_correct: false, sort_order: 2 },
        ],
      };

      return {
        ...prev,
        questions: [...prev.questions, newQuestion],
      };
    });
  };

  // const handleQuestionChange = (index, field, value) => {
  //   const updated = [...formData.questions];
  //   updated[index][field] = value;
  //   setFormData({ ...formData, questions: updated });
  // };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...formData.questions];
    const current = updated[index];

    // Logic for nullifying conflicting fields
    if (field === "type") {
      updated[index][field] = value;
      if (value === "mcq") {
        updated[index].answer_text = null;
        if (!Array.isArray(updated[index].options)) {
          updated[index].options = [
            { option_text: "", is_correct: false, sort_order: 1 },
            { option_text: "", is_correct: false, sort_order: 2 },
          ];
        }
      } else if (value === "text") {
        updated[index].options = [];
      }
    } else {
      updated[index][field] = value;
    }

    setFormData({ ...formData, questions: updated });
  };

  const handleOptionChange = (qIndex, oIndex, field, value) => {
    const updated = [...formData.questions];
    updated[qIndex].options[oIndex][field] = value;
    setFormData({ ...formData, questions: updated });
  };

  const handleRemoveQuestion = (index) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updated });
  };

  const handleAddOption = (qIndex) => {
    const updated = [...formData.questions];
    const newSortOrder = updated[qIndex].options.length + 1;

    updated[qIndex].options.push({
      option_text: "",
      is_correct: false,
      sort_order: newSortOrder, // 👈 auto-set here
    });

    setFormData({ ...formData, questions: updated });
  };

  const handleRemoveOption = (qIndex, oIndex) => {
    const updated = [...formData.questions];
    updated[qIndex].options.splice(oIndex, 1);
    setFormData({ ...formData, questions: updated });
  };

  const clear = () => {
    setFormData({
      title: "",
      description: "",
      total_marks: 0,
      total_questions: 0,
      questions: [],
      company_id: company_id,
      created_by: user_id,
    });
    setIsEdit(false);
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }], // ✅ Text and background color
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"], // remove formatting
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "video",
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchBy, setSearchBy] = useState("title");
  const [searchValue, setSearchValue] = useState("");
  const [assessment, setAssessment] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchAssessment = async (search = searchValue) => {
    try {
      setLoading(true);
      const dataToSend = {
        company_id: company_id,
        search_by: searchBy,
        search: search,
        per_page: rowsPerPage,
        page: page + 1, // Laravel pagination is 1-based
      };

      const res = await configServ.getAssessmentsByCompanyId(dataToSend);
      if (res.success) {
        setAssessment(res.data.data); // Assuming `data` is paginated
        setCount(res.data.total);
      } else {
        setAssessment([]);
        setCount(0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  // Debounce wrapper
  const debouncedSearch = useCallback(
    debounce((val) => {
      setPage(0);
      fetchAssessment(val);
    }, 500),
    []
  );

  // Update search value
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  // Fetch when pagination changes
  useEffect(() => {
    fetchAssessment();
  }, [page, rowsPerPage, searchBy]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page on rowsPerPage change
  };
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  useEffect(() => {
    if (isEdit && isCreate && firstEditLoad.current && selectedRowForEdit) {
      const row = selectedRowForEdit;

      const formattedQuestions = row.questions.map((q) => ({
        question_text: q.question_text || "",
        answer_text: q.answer_text || "",

        marks: q.marks || 0,
        negative_marks: q.negative_marks || 0,
        type: q.question_type || "mcq",
        sort_order: q.sort_order || 1,
        options: q.options.map((opt) => ({
          option_text: opt.option_text || "",
          is_correct: !!opt.is_correct,
          sort_order: opt.sort_order || 1,
        })),
      }));

      setFormData({
        id: row.id || "",
        title: row.title || "",
        description: row.description || "",
        total_marks: row.total_marks || 0,
        total_questions: row.total_questions || 0,
        company_id: row.company_id,
        created_by: row.created_by,
        questions: formattedQuestions,
      });

      firstEditLoad.current = false; // Prevent rerun
    }
  }, [isEdit, isCreate, selectedRowForEdit]);

  const handleEdit = (row) => {
    setIsCreate(true);
    setIsEdit(true);
    setSelectedRowForEdit(row); // only trigger effect
    console.log(row);
    firstEditLoad.current = true;
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: `Delete assessment "${row.title}"?`,
      text: "This will delete all its questions and options. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const res = await configServ.deleteWithQuestions(row.id);
        Swal.fire("Deleted!", "The assessment has been deleted.", "success");
        fetchAssessment(); // or any function to reload the table data
      } catch (error) {
        Swal.fire("Error!", "Failed to delete the assessment.", "error");
        console.error(error);
      }
    }
  };

  // Helper to strip HTML
  const stripHTML = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };
  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };
  const validateForm = () => {
    const errors = [];

    const strippedTitle = stripHtml(formData.title || "").trim();
    const strippedDescription = stripHtml(formData.description || "").trim();

    if (!strippedTitle) errors.push("Title is required.");
    if (!strippedDescription) errors.push("Description is required.");

    formData.questions.forEach((q, i) => {
      const strippedQuestionText = stripHtml(q.question_text || "").trim();
      if (!strippedQuestionText)
        errors.push(`Question ${i + 1} text is required.`);

      if (q.type === "mcq") {
        if (!q.options || q.options.length < 2) {
          errors.push(`Question ${i + 1} must have at least 2 options.`);
        } else {
          const validOptions = q.options.filter(
            (opt) => (opt.option_text || "").trim() !== ""
          );
          if (validOptions.length < 2) {
            errors.push(
              `Question ${i + 1} must have at least 2 non-empty options.`
            );
          }
        }
      }

      if (q.type === "text") {
        const strippedAnswer = stripHtml(q.answer_text || "").trim();
        if (!strippedAnswer) {
          errors.push(`Question ${i + 1} must have a correct answer.`);
        }
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: errors.join("<br>"),
      });
      return;
    }

    const totalMarks = formData.questions.reduce(
      (sum, q) => sum + Number(q.marks || 0),
      0
    );
    const totalQuestions = formData.questions.length;

    const payload = {
      ...formData,
      total_marks: totalMarks,
      total_questions: totalQuestions,
    };

    console.log("Payload to be sent:", payload);

    try {
      let res;
      if (isEdit) {
        res = await configServ.updateWithQuestions(formData.id, payload);
      } else {
        res = await configServ.storeWithQuestions(payload);
      }

      if (res.success) {
        setIsCreate(false);
        clear();
        fetchAssessment();
        await Swal.fire({
          icon: "success",
          title: `Assessment ${isEdit ? "updated" : "created"} successfully!`,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: `Failed to ${isEdit ? "update" : "create"} assessment.`,
        text:
          err?.response?.data?.message ||
          err.message ||
          "Something went wrong.",
      });
      console.error(err);
    }
  };
  const [viewData, setViewData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleView = (row) => {
    setViewData(row);
    setDrawerOpen(true);
  };

  return (
    <>
    <AssessmentViewDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} data={viewData} />

      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Create Assessment"}
        handlePopup={handlePopup}
        isEdit={isEdit}
        create={isCreate}
      />
      {isCreate === true && (
        <Container>
          <motion.div
            style={{
              width: "100%",
              backgroundColor: "#e3f3fc",
              padding: 20,
              borderRadius: "10px",
              border: "none",
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.1, delay: 0.1 }}
          >
            <Grid container spacing={2}>
              {/* Title */}
              <Grid item xs={12} md={12}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Assessment Title
                </label>
                <ReactQuill
                  theme="snow"
                  value={formData.title || ""}
                  onChange={(value) => {
                    if (formData.title !== value) {
                      setFormData((prev) => ({ ...prev, title: value }));
                    }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{
                    backgroundColor: "white",
                    minHeight: "120px",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                />
                <style>
                  {`
                    .ql-toolbar {
                      position: sticky;
                      top: 0;
                      z-index: 1;
                      background: white;
                     }
                 `}
                </style>
                {/* {validationErrors.title && (
                <p
                  style={{
                    color: "red",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {validationErrors.title}
                </p>
              )} */}
              </Grid>

              {/* Description */}
              <Grid item xs={12} md={12}>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  Description
                </label>
                <ReactQuill
                  theme="snow"
                  value={formData.description || ""}
                  onChange={(value) => {
                    if (formData.description !== value) {
                      setFormData((prev) => ({ ...prev, description: value }));
                    }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{
                    backgroundColor: "white",
                    minHeight: "120px",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                />
                <style>
                  {`
                .ql-toolbar {
                  position: sticky;
                  top: 0;
                  z-index: 1;
                  background: white;
                }
             `}
                </style>
                {/* {validationErrors.description && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {validationErrors.description}
                  </p>
                )} */}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Questions List */}
            {formData.questions.map((q, i) => (
              <QuestionBlock
                key={i}
                index={i}
                question={q}
                onChange={handleQuestionChange}
                onRemove={handleRemoveQuestion}
                onOptionChange={handleOptionChange}
                onAddOption={handleAddOption}
                onRemoveOption={handleRemoveOption}
                totalQuestions={formData.questions.length} // ✅ pass this
              />
            ))}

            {/* Bottom Buttons */}
            <Grid container spacing={2} mt={2} justifyContent="space-between">
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                >
                  Add Question
                </Button>
              </Grid>

              {/* <Grid item>
                <Button variant="outlined" color="error" onClick={clear}>
                  Clear All
                </Button>
              </Grid> */}

              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={formData.questions.length === 0}
                >
                  {isEdit ? <>Update</> : <>Submit Assessment</>}
                </Button>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      )}
      <CustomTable
        columns={[
          {
            key: "title",
            label: "Title",
            render: (row) => stripHTML(row.title),
          },
          {
            key: "description",
            label: "Description",
            render: (row) => stripHTML(row.description),
          },
          { key: "total_marks", label: "Total Marks" },
          { key: "total_questions", label: "Total Questions" },
          {
            key: "actions",
            label: "Actions",
            render: (row) => (
              <>
                <IconButton color="primary" onClick={() => handleEdit(row)}>
                  <Edit />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(row)}>
                  <DeleteForever />
                </IconButton>
                <IconButton color="primary" onClick={() => handleView(row)}>
                  <Visibility />
                </IconButton>
              </>
            ),
          },
        ]}
        rows={assessment}
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        searchBy={searchBy}
        onSearchByChange={(e) => {
          setSearchBy(e.target.value);
          setPage(0); // important: reset pagination
        }}
        searchValue={searchValue}
        onSearchValueChange={handleSearchChange}
        searchOptions={[
          { label: "Title", value: "title" },
          // { label: "Description", value: "description" },
        ]}
        loading={loading}
      />
    </>
  );
};

export default CreateAssessmentPage;
